import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from '@nanostores/react';
import type { WebContainer } from '@webcontainer/api';
import { Button, Checkbox, Input, Label, cn } from '@ideoz/ui';
import { workbenchStore } from '../stores/workbench';
import { selectFile } from '../stores/editor';
import { getMergedFilePaths, getMergedFileContent } from '../utils/mergedFiles';
import { persistUserFile } from '../runtime/tool-adapters/writeFile';

export interface SearchHit {
  path: string;
  line: number;
  preview: string;
}

function buildSearchPattern(
  query: string,
  matchCase: boolean,
  wholeWord: boolean,
  asRegex: boolean,
): RegExp | null {
  if (!query.trim()) return null;
  try {
    if (asRegex) {
      return new RegExp(query, matchCase ? 'gm' : 'gim');
    }
    let body = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (wholeWord) body = `\\b${body}\\b`;
    return new RegExp(body, matchCase ? 'g' : 'gi');
  } catch {
    return null;
  }
}

function lineMatches(re: RegExp, line: string): boolean {
  const flags = re.flags.replace('g', '');
  const lineRe = new RegExp(re.source, flags);
  return lineRe.test(line);
}

function scan(query: string, matchCase: boolean, wholeWord: boolean, asRegex: boolean): SearchHit[] {
  const re = buildSearchPattern(query, matchCase, wholeWord, asRegex);
  if (!re) return [];
  const paths = getMergedFilePaths();
  const hits: SearchHit[] = [];
  for (const path of paths) {
    const content = getMergedFileContent(path);
    const lines = content.split(/\n/);
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]!;
      if (lineMatches(re, line)) {
        hits.push({
          path,
          line: i + 1,
          preview: line.length > 140 ? `${line.slice(0, 137)}…` : line,
        });
      }
    }
  }
  return hits;
}

export interface SearchReplacePanelProps {
  webcontainer: WebContainer | null;
}

export function SearchReplacePanel({ webcontainer }: SearchReplacePanelProps) {
  const files = useStore(workbenchStore.files);
  const [query, setQuery] = useState('');
  const [replace, setReplace] = useState('');
  const [matchCase, setMatchCase] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [asRegex, setAsRegex] = useState(false);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = scan(query, matchCase, wholeWord, asRegex);
      setHits(next);
      setActiveIdx(0);
    }, 220);
    return () => window.clearTimeout(t);
  }, [query, matchCase, wholeWord, asRegex, files]);

  const replacePattern = useMemo(
    () => buildSearchPattern(query, matchCase, wholeWord, asRegex),
    [query, matchCase, wholeWord, asRegex],
  );

  const replaceAll = useCallback(async () => {
    if (!replacePattern || !query.trim()) return;
    setBusy(true);
    try {
      const paths = getMergedFilePaths();
      if (!webcontainer) {
        for (const path of paths) {
          const content = getMergedFileContent(path);
          const next = content.replace(replacePattern, replace);
          if (next !== content) {
            workbenchStore.files.setKey(path, { content: next, modified: true });
          }
        }
      } else {
        for (const path of paths) {
          const content = getMergedFileContent(path);
          const next = content.replace(replacePattern, replace);
          if (next !== content) {
            // `true`: this is a user-initiated edit, same as the code editor's save and
            // Hot Edit's write — it must be protected from a silent AI overwrite and
            // included as manual-edit context on the next chat turn, exactly like those.
            await persistUserFile(webcontainer, path, next, true);
          }
        }
      }
      setHits(scan(query, matchCase, wholeWord, asRegex));
    } finally {
      setBusy(false);
    }
  }, [replacePattern, query, replace, matchCase, wholeWord, asRegex, webcontainer]);

  const replaceInActiveFile = useCallback(async () => {
    if (!replacePattern || !query.trim() || hits.length === 0) return;
    const hit = hits[Math.min(activeIdx, hits.length - 1)]!;
    const content = getMergedFileContent(hit.path);
    const next = content.replace(replacePattern, replace);
    if (next === content) return;
    setBusy(true);
    try {
      if (!webcontainer) {
        workbenchStore.files.setKey(hit.path, { content: next, modified: true });
      } else {
        // See replaceAll above — this is a user-initiated edit and must be marked modified.
        await persistUserFile(webcontainer, hit.path, next, true);
      }
      setHits(scan(query, matchCase, wholeWord, asRegex));
    } finally {
      setBusy(false);
    }
  }, [replacePattern, query, replace, webcontainer, hits, activeIdx, matchCase, wholeWord, asRegex]);

  return (
    <div className="flex flex-col h-full min-h-0 glass-subtle border-r border-border/40">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-border/40 shrink-0">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Search</span>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={busy || !replacePattern || hits.length === 0}
            onClick={() => void replaceInActiveFile()}
          >
            Replace
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 text-xs"
            disabled={busy || !replacePattern || !query.trim()}
            onClick={() => void replaceAll()}
          >
            Replace all
          </Button>
        </div>
      </div>
      <div className="p-2 space-y-2 overflow-y-auto flex-1 min-h-0">
        <div>
          <Input
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9 text-sm font-mono"
          />
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
              <Checkbox checked={matchCase} onCheckedChange={(v) => setMatchCase(!!v)} />
              Match case
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
              <Checkbox checked={wholeWord} onCheckedChange={(v) => setWholeWord(!!v)} />
              Whole word
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
              <Checkbox checked={asRegex} onCheckedChange={(v) => setAsRegex(!!v)} />
              Regex
            </label>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground sr-only">Replace</Label>
          <Input
            placeholder="Replace"
            value={replace}
            onChange={(e) => setReplace(e.target.value)}
            className="h-9 text-sm font-mono"
          />
        </div>
        <div className="text-[11px] text-muted-foreground">
          {hits.length === 0 && query.trim() ? 'No results' : `${hits.length} result${hits.length === 1 ? '' : 's'}`}
        </div>
        <ul className="space-y-0.5 max-h-[50vh] overflow-y-auto pr-0.5">
          {hits.map((h, i) => (
            <li key={`${h.path}:${h.line}:${i}`}>
              <button
                type="button"
                onClick={() => {
                  setActiveIdx(i);
                  selectFile(h.path);
                }}
                className={cn(
                  'w-full text-left px-2 py-1 rounded-md text-[11px] font-mono transition-colors',
                  i === activeIdx ? 'bg-primary/10 text-foreground' : 'hover:bg-muted/60 text-muted-foreground',
                )}
              >
                <div className="truncate text-primary-800">{h.path}</div>
                <div className="truncate opacity-90">
                  L{h.line}: {h.preview}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
